import { Viewport } from '@/enums/viewport';
import { useResizeObserver, useScroll } from '@vueuse/core';
import { Ref, computed, onActivated, onDeactivated, onMounted, readonly, ref, watch } from 'vue';
import { Route } from 'vue-router';
import { useRoute, useRouter } from 'vue-router/composables';
import { getViewport } from '../ui-helper';
import { parsePageNumber } from './usePagination';

const ANCHOR_NAME = 'jw_page_anchor' as const;

function getPageQueryParam(routeQuery: Route['query']) {
	const page = (routeQuery.page as unknown as string) || '1';
	return parsePageNumber(page);
}

function getThresholdValue(viewport: Viewport): number {
	const { innerHeight } = window;
	switch (viewport) {
		case 'tablet':
			return innerHeight * 1.5;
		case 'smartphone':
			return innerHeight * 2.6;
		default:
			return innerHeight * 0.8;
	}
}

// we don't need to create a full blown Event.
// we only need to support `event.target.complete()` calls, that are called after a loadMore()
type LoadMoreEvent = { target: { complete: () => void } };
export const createLoadMoreEvent = (onComplete: () => void): LoadMoreEvent => ({
	target: { complete: onComplete },
});

export interface InfiniteBiScrollProps {
	enabled: Ref<boolean>;
	loading: Ref<boolean>;
	pageSize: Ref<number>;
	hardPageLimit: Ref<number>;
	titlesAmount: Ref<number>;
}

const DEFAULT_RETURNS = {
	page: ref(0),
	pageStart: readonly(computed(() => 0)),
	lastPage: computed(() => 0),
	loadingPrev: readonly(ref(false)),
	loadingNext: readonly(ref(false)),
	anyLoading: ref(false),
	ANCHOR_NAME: '' as string,
} as const;

export function useBiScroll(
	el: Ref<HTMLElement | null>,
	{ loading, pageSize, hardPageLimit, titlesAmount, enabled }: InfiniteBiScrollProps,
	onEmit: (e: 'load-next' | 'load-prev', payload: { event: LoadMoreEvent; page: number }) => void
): typeof DEFAULT_RETURNS {
	const route = useRoute();
	const router = useRouter();

	const activated = ref(true);
	const initialised = ref(false);
	const positionsByAnchor = ref<number[]>([]);
	const page = ref(getPageQueryParam(route.query));
	const pageStart = ref(page.value); // lowest page query param

	const lastPage = computed(() => {
		const last = Math.ceil(titlesAmount.value / pageSize.value);
		const maxPage = hardPageLimit.value;
		return last > maxPage ? maxPage : last;
	});
	const loadingPrev = ref(false);
	const loadingNext = ref(page < lastPage);
	const anyLoading = computed<boolean>(() => loading.value || loadingPrev.value || loadingNext.value);

	// upage `page` query param as with window.replace, so it doesn't add a history state.
	// the user will scroll from up and down, load new titles on top and bottom. in all these cases,
	// the `page` param will update accordingly.
	function replacePageQueryParam() {
		if (page.value > 1) {
			if (route.query.path !== page.value + '') {
				// update page parameter
				router.replace({ path: route.path, query: { ...route.query, page: page.value + '' } });
			}
		} else if (page.value <= 1) {
			if (route.query.page) {
				// remove page parameter
				// eslint-disable-next-line @typescript-eslint/no-unused-vars
				const { page, ...query } = route.query;
				router.replace({ path: route.path, query });
			}
		}
	}

	// find the lowest page.
	// since we can load more content in both direction, it's important know where the page number starts,
	// so the anchor IDs can be calculated accordingly.
	function updateTheLowestPage() {
		const lowest = Math.min(page.value, pageStart.value);
		if (lowest < pageStart.value) {
			pageStart.value = lowest;
		}
	}

	function updatePageParam(scrollY: number) {
		// it's possible that enabled changes during filtering (with query parameters or without)
		if (!enabled.value) {
			return;
		}

		// there is a possibility that the mounting happens before the useResizeObserver calculates.
		if (!initialised.value || !activated.value) {
			return;
		}
		const position = scrollY + window.screen.height;
		const activePage = positionsByAnchor.value.reduce((root, value, index) => {
			return value < position ? pageStart.value - 1 + index : root;
		}, 0);
		// update current page
		if (page.value != activePage + 1) {
			page.value = activePage + 1;

			replacePageQueryParam();
			updateTheLowestPage();
		}
	}

	onMounted(() => {
		// if page parameter is used, scroll into view so the user can scroll up to `loadLess()` titles.
		if (page.value > 1 && enabled.value) {
			window.scrollBy(0, 20);
		}

		const scroll = useScroll(document, {
			throttle: 100,
			behavior: 'smooth',
			offset: { bottom: getThresholdValue(getViewport()) },
		});

		// infinite scroll implementation by using useScroll.
		// because useInfiniteScroll seems to be buggy in many versions of @vueuse.
		watch(scroll.arrivedState, state => {
			// it's possible that enabled changes during filtering (with query parameters or without)
			if (!enabled.value) {
				return;
			}

			if (anyLoading.value || !activated.value) {
				return;
			}

			if (state.top && page.value != 1) {
				loadingPrev.value = true;
				const { height } = el.value?.getBoundingClientRect()!;
				const onComplete = () => {
					pageStart.value = page.value - 1;
					loadingPrev.value = false;
					// restore scroll position because content is prepended
					const { height: newHeight } = el.value?.getBoundingClientRect()!;
					const withPlaceholderDiv = page.value === 1 ? 20 : 0;
					window.scrollBy(0, window.scrollY + (newHeight - height) - withPlaceholderDiv);
				};

				const event = createLoadMoreEvent(onComplete);
				onEmit('load-prev', { event, page: page.value - 1 });
			}

			if (state.bottom && page.value + 1 <= lastPage.value) {
				loadingNext.value = true;
				const onComplete = () => {
					loadingNext.value = false;
				};
				const event = createLoadMoreEvent(onComplete);
				onEmit('load-next', { event, page: page.value + 1 });
			}
		});

		watch(scroll.y, updatePageParam);

		useResizeObserver(el, () => {
			if (!activated.value || !enabled.value) {
				return;
			}

			// content of infinite scroll changed either by:
			// - resizing window
			// - rotating phone
			// - new items added through loadMore/loadLess
			const anchors = el.value?.querySelectorAll(`[data-page="${ANCHOR_NAME}"]`)!;
			const positions = [...anchors].map(item => item.getBoundingClientRect().top + window.scrollY);
			positionsByAnchor.value = positions;

			if (!initialised.value) {
				initialised.value = true;
			}
		});
	});

	// make sure we don't update query params while different views are active
	onActivated(() => {
		activated.value = true;
	});
	onDeactivated(() => {
		activated.value = false;
	});

	return {
		page,
		lastPage,
		pageStart: readonly(pageStart),
		loadingPrev: readonly(loadingPrev),
		loadingNext: readonly(loadingNext),
		anyLoading,
		ANCHOR_NAME,
	};
}
